import { API_ERROR_CODES } from '@gozoki/api-types'
import { formatInteger, isFrenchSingular, pluralWord } from '@gozoki/tools'
import axios from 'axios'

// `cn` vit désormais dans @gozoki/ui (source unique, partagée avec store-app).
// Les formateurs métier ci-dessous restent locaux au back-office.
export { cn } from '@gozoki/ui/lib/utils'

/**
 * Suffixe pluriel français (`''` ou `'s'`) selon `count`, règle de `isFrenchSingular`.
 * En français, 0 et 1 sont singuliers (« 0 permission manquante »).
 * Ex. : `Permission${frenchPluralSuffix(n)} manquante${frenchPluralSuffix(n)}`.
 */
export function frenchPluralSuffix(count: number): '' | 's' {
  return isFrenchSingular(count) ? '' : 's'
}

export function formatPercentage(
  percentage: number,
  options?: { withSign?: boolean; withPercent?: boolean },
) {
  const { withSign = true, withPercent = true } = options ?? {}

  let result = ''
  if (Number.isNaN(percentage) || percentage === Number.NEGATIVE_INFINITY) {
    result = '0'
  } else if (percentage === Number.POSITIVE_INFINITY) {
    result = '100'
  } else {
    result = percentage < 10 ? percentage.toFixed(1) : formatInteger(percentage)
  }

  result = `${result.replace('.', ',')}${withPercent ? '%' : ''}`

  if (withSign) {
    result = `${percentage > 0 ? '+' : ''}${result}`
  }

  return result
}

/** Message d'un refus sans explication métier (`throwForbidden`, `RbacForbiddenException`). */
export const GENERIC_FORBIDDEN_MESSAGE = 'Forbidden'

/** Repli d'un écran qui n'en donne pas. */
export const DEFAULT_API_ERROR_MESSAGE =
  'Une erreur inconnue est survenue, veuillez contacter le support'

/** Repli d'un écran qui n'en donne pas, sur un refus (403) sans motif. */
export const DEFAULT_FORBIDDEN_ERROR_MESSAGE = "Vous n'avez pas les droits pour cette action"

/** Messages du backend qui n'expliquent rien : refus sans motif, erreur serveur en production. */
const UNEXPLAINED_MESSAGES = new Set([GENERIC_FORBIDDEN_MESSAGE, 'Something went wrong'])

/** Réponse en échec d'un appel API : statut, et corps (absent sur une erreur réseau). */
interface ApiErrorResponse {
  status: number | null
  body: unknown
}

interface ApiErrorMessageOptions {
  /**
   * La route explique ses erreurs serveur (DVR injoignable, caisse sans transaction en cours) :
   * leur message est montré, sauf « Something went wrong », qui ne dit rien.
   */
  showServerMessage?: boolean
}

/** Réponse d'une erreur d'appel API, client axios (legacy) ou Tuyau ; `null` hors appel API. */
const apiErrorResponseOf = (error: unknown): ApiErrorResponse | null => {
  if (axios.isAxiosError(error)) {
    return { status: error.response?.status ?? null, body: error.response?.data }
  }
  if (!error || typeof error !== 'object' || !('response' in error)) return null
  return { status: getTuyauErrorStatus(error), body: error.response }
}

/** Premier texte du corps : `message`, puis `error` (corps hérité), puis `errors[0].message` (422). */
const messageOfBody = (body: object): string | null => {
  const { message, error, errors } = body as {
    message?: unknown
    error?: unknown
    errors?: unknown
  }
  const first: unknown = Array.isArray(errors) ? errors[0] : undefined
  const firstMessage = first && typeof first === 'object' && 'message' in first && first.message
  for (const candidate of [message, error, firstMessage]) {
    if (typeof candidate === 'string' && candidate) return candidate
  }
  return null
}

/** `code` du contrat `{ code, message }`, `null` sans code. */
const codeOfBody = (body: object): string | null =>
  'code' in body && typeof body.code === 'string' ? body.code : null

const messageOfResponse = (
  { status, body }: ApiErrorResponse,
  screenFallback: string | undefined,
  { showServerMessage = false }: ApiErrorMessageOptions,
): string => {
  const fallbackMessage =
    screenFallback ?? (status === 403 ? DEFAULT_FORBIDDEN_ERROR_MESSAGE : DEFAULT_API_ERROR_MESSAGE)
  if (!body || typeof body !== 'object') return fallbackMessage
  // Contrat `{ code, message }` : seul `internal` est une erreur serveur sans explication ; les
  // autres codes d'un 5xx (`upstream_unavailable`…) portent un message écrit pour être montré.
  const code = codeOfBody(body)
  const serverError =
    code === null ? status !== null && status >= 500 : code === API_ERROR_CODES.internal
  if (serverError && !showServerMessage) return fallbackMessage
  const message = messageOfBody(body)
  return message === null || UNEXPLAINED_MESSAGES.has(message) ? fallbackMessage : message
}

/**
 * Message d'une erreur à montrer à l'utilisateur, seul helper d'erreur du back-office.
 *
 * Erreur d'un appel API (client Tuyau, ou axios legacy) : le corps se lit par son `code` (contrat
 * `{ code, message }`), puis son `message`, puis `error` et `errors[0].message` (corps hérités).
 * Le repli `fallbackMessage` (français) de l'écran remplace ce qui n'explique rien : une erreur
 * serveur (`code: 'internal'`, ou un 5xx au corps hérité ; cf. `showServerMessage`), le « Forbidden »
 * d'un refus sans motif, une réponse sans message (erreur réseau, page HTML). Sans repli de
 * l'écran : `DEFAULT_FORBIDDEN_ERROR_MESSAGE` sur un 403, `DEFAULT_API_ERROR_MESSAGE` ailleurs.
 *
 * Erreur levée par l'écran lui-même (hors appel API) : son propre message.
 */
export function getApiErrorMessage(
  error: unknown,
  fallbackMessage?: string,
  options: ApiErrorMessageOptions = {},
): string {
  const response = apiErrorResponseOf(error)
  if (response) return messageOfResponse(response, fallbackMessage, options)
  if (error instanceof Error && error.message) return error.message
  return fallbackMessage ?? DEFAULT_API_ERROR_MESSAGE
}

/**
 * Code du contrat `{ code, message }` d'une erreur d'appel API (`API_ERROR_CODES` ou code métier de
 * la route), pour adapter la réaction de l'écran. `null` sans code : corps hérité, erreur réseau,
 * erreur levée par l'écran.
 */
export function getApiErrorCode(error: unknown): string | null {
  const body = apiErrorResponseOf(error)?.body
  return body && typeof body === 'object' ? codeOfBody(body) : null
}

/**
 * `getApiErrorMessage` d'un téléchargement `responseType: 'blob'` (client axios legacy) : le corps
 * d'erreur y arrive en `Blob`, lu ici comme du JSON (illisible : repli de l'écran).
 */
export async function getApiErrorMessageFromBlob(
  error: unknown,
  fallbackMessage?: string,
): Promise<string> {
  if (!axios.isAxiosError(error) || !(error.response?.data instanceof Blob)) {
    return getApiErrorMessage(error, fallbackMessage)
  }
  const { status, data } = error.response
  return messageOfResponse({ status, body: await parseJsonBlob(data) }, fallbackMessage, {})
}

const parseJsonBlob = async (blob: Blob): Promise<unknown> => {
  try {
    return JSON.parse(await blob.text()) as unknown
  } catch {
    return null
  }
}

/**
 * Statut HTTP de la réponse en erreur : client Tuyau (`TuyauHTTPError.status`) ou axios
 * (`error.response.status`, legacy). `null` quand la requête n'a pas eu de réponse (réseau coupé,
 * délai dépassé : `TuyauNetworkError`, axios sans `response`) ou pour toute autre erreur.
 */
export function getTuyauErrorStatus(error: unknown): number | null {
  if (axios.isAxiosError(error)) return error.response?.status ?? null
  if (!error || typeof error !== 'object' || !('status' in error)) return null
  const { status } = error as { status?: unknown }
  return typeof status === 'number' ? status : null
}

/** Délai (secondes, > 0) d'un 429 qui porte `retryAfter` ou `retryAfterSec` dans son corps ; `null` sinon. */
export function getTuyauRetryAfterSeconds(error: unknown): number | null {
  if (getTuyauErrorStatus(error) !== 429) return null
  const body =
    error && typeof error === 'object' && 'response' in error
      ? (error as { response?: { retryAfter?: unknown; retryAfterSec?: unknown } }).response
      : undefined
  const retryAfter = body?.retryAfter ?? body?.retryAfterSec
  return typeof retryAfter === 'number' && retryAfter > 0 ? retryAfter : null
}

export interface ApiFieldError<Field extends string> {
  field: Field
  message: string
}

/** `errors` d'un corps 422, lu comme le message (`apiErrorResponseOf`, client Tuyau ou axios). */
const validationErrorsOf = (error: unknown): unknown[] | null => {
  const body = apiErrorResponseOf(error)?.body
  if (!body || typeof body !== 'object' || !('errors' in body)) return null
  return Array.isArray(body.errors) ? body.errors : null
}

/** Entrées bien formées d'une réponse 422, avec leur règle VineJS (`null` sans elle). */
const validationEntriesOf = (
  error: unknown,
): (ApiFieldError<string> & { rule: string | null })[] => {
  const result: (ApiFieldError<string> & { rule: string | null })[] = []
  for (const entry of validationErrorsOf(error) ?? []) {
    if (!entry || typeof entry !== 'object') continue
    const { field, message, rule } = entry as { field?: unknown; message?: unknown; rule?: unknown }
    if (typeof field === 'string' && typeof message === 'string') {
      result.push({ field, message, rule: typeof rule === 'string' ? rule : null })
    }
  }
  return result
}

/**
 * Entrées `{ field, message }` d'une réponse 422 VineJS (`{ errors: [...] }`), d'une erreur Tuyau
 * comme d'une erreur axios (`@gozoki/api`, legacy), chemins complets conservés (`demand.12.palis`,
 * `hubIds.0`) ; les entrées mal formées sont ignorées. Vide hors réponse de validation.
 */
export function getApiValidationErrors(error: unknown): ApiFieldError<string>[] {
  return validationEntriesOf(error).map(({ field, message }) => ({ field, message }))
}

/**
 * Règle VineJS (`rule`) du premier refus 422 sur `field`, parmi les mêmes entrées que
 * `getApiValidationErrors`, quand deux refus d'un même champ appellent des messages différents.
 * `null` sans refus ni règle.
 */
export function getApiValidationRule(error: unknown, field: string): string | null {
  return validationEntriesOf(error).find((entry) => entry.field === field)?.rule ?? null
}

/**
 * Erreurs de champ d'une réponse 422 (cf. `getApiValidationErrors`) rapportées aux champs d'un
 * formulaire : `fields` est soit la table champ de l'API → champ du formulaire, soit la liste des
 * champs du formulaire qui portent le même nom côté API. Un champ inconnu est ignoré (il part en
 * toast chez l'appelant) ; un chemin se lit par son plus long préfixe connu : `channels.0` (élément
 * d'un tableau) se lit `channels`, `driver.carrierId` (champ d'un bloc) se lit `driver.carrierId`
 * s'il est connu, `driver` sinon.
 */
export function getApiFieldErrors<Field extends string>(
  error: unknown,
  fields: Readonly<Record<string, Field>> | readonly Field[],
): ApiFieldError<Field>[] {
  const result: ApiFieldError<Field>[] = []
  for (const { field: apiField, message } of getApiValidationErrors(error)) {
    const field = formFieldOfApiField(apiField, fields)
    if (field !== undefined) result.push({ field, message })
  }
  return result
}

/** Champ du formulaire d'un champ d'erreur de l'API (même règle que `getApiFieldErrors`). */
export function formFieldOfApiField<Field extends string>(
  apiField: string,
  fields: Readonly<Record<string, Field>> | readonly Field[],
): Field | undefined {
  const fieldOf = (path: string): Field | undefined => {
    if (Array.isArray(fields)) {
      return (fields as readonly string[]).includes(path) ? (path as Field) : undefined
    }
    const table = fields as Readonly<Record<string, Field>>
    return Object.hasOwn(table, path) ? table[path] : undefined
  }
  const segments = apiField.split('.')
  for (let length = segments.length; length > 0; length -= 1) {
    const field = fieldOf(segments.slice(0, length).join('.'))
    if (field !== undefined) return field
  }
  return undefined
}

export function trimSeconds<T extends string | null>(time: T): T {
  if (!time) return time
  return time.slice(0, 5) as T
}

// Nombres, euros et pourcentages : module partagé de `@gozoki/tools` (`formatInteger`,
// `formatWholeEuros`, `formatCentsAsWholeEuros`, `formatKiloEuros`, `formatPercent`…).

// Forme d'un mot selon `count`, même règle que `frenchPluralSuffix` : partagée avec le backend.
export { pluralWord }

/** `count` suivi du mot accordé (cf. `pluralWord`) : « 1 hub », « 3 hubs », « 0 boutique ». */
export const pluralize = (count: number, singular: string, plural?: string): string =>
  `${count} ${pluralWord(count, singular, plural)}`

const frenchListFormat = new Intl.ListFormat('fr-FR', { type: 'conjunction' })

/** Une énumération en français : « Marc », « Marc et Julie », « Marc, Julie et Tom ». */
export const frenchList = (items: readonly string[]): string => frenchListFormat.format(items)
